import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
    static values = {
        storageKey: { type: String, default: "sidebar_open_item" }
    }

    static targets = ["defaultOpen"]

    connect() {
        this.isTogglingMenu = false
        this.initializeExpandFields()
        if (this.shouldOpenCurrentSection()) {
            this.openDefaultSection()
        }
        this.setupResizeObserver()
    }

    disconnect() {
        this.removeResizeObserver()
    }

    setupResizeObserver() {
        this.resizeObserver = new ResizeObserver(entries => {
            // Ignore resize events during toggle animations
            if (this.isTogglingMenu) return

            entries.forEach(entry => {
                const expandField = entry.target.closest('.expand-field')
                if (expandField && this.isOpen(expandField)) {
                    this.recalculateHeight(expandField)
                }
            })
        })

        // Observe all expand fields
        const expandFields = this.element.querySelectorAll(".expand-field")
        expandFields.forEach(field => {
            this.resizeObserver.observe(field)
        })
    }

    removeResizeObserver() {
        if (this.resizeObserver) {
            this.resizeObserver.disconnect()
        }
    }

    isOpen(field) {
        return field.style.maxHeight &&
            field.style.maxHeight !== "0px" &&
            field.style.maxHeight !== "0"
    }

    recalculateHeight(field) {
        const originalTransition = field.style.transition
        field.style.transition = 'none'

        // Recalculate height
        field.style.maxHeight = 'none'
        const newHeight = field.scrollHeight
        field.style.maxHeight = newHeight + 'px'

        // Restore transition after a short delay
        requestAnimationFrame(() => {
            field.style.transition = originalTransition
        })
    }

    shouldOpenCurrentSection() {
        return this.hasDefaultOpenTarget && (
            this.getSidebarItemUrl("/archiv") || this.getSidebarItemUrl("/vermietungen") || this.getSidebarItemUrl("/stiftung")
        )
    }

    openDefaultSection() {
        this.defaultOpenTargets.forEach((target) => {
            const expandField = target.querySelector(".expand-field")
            if (expandField) {
                const shouldOpen = ((this.getSidebarItemUrl("/archiv") && target.classList.contains("internal_link_10")) ||
                    (this.getSidebarItemUrl("/vermietungen") && target.classList.contains("internal_link_3")) ||
                    (this.getSidebarItemUrl("/stiftung") && (target.classList.contains("internal_link_8")))
                )

                if (shouldOpen) {
                    this.openExpandField(expandField)
                    const index = target.getAttribute("data-sidebar-index")
                    if (index) this.saveOpenState(index)
                }
            }
        })
    }

    getSidebarItemUrl(path) {
        return window.location.pathname.includes(path)
    }

    initializeExpandFields() {
        const elements = this.element.querySelectorAll(".expand-field")
        elements.forEach((el, index) => {
            el.style.overflow = "hidden"
            el.style.transition = "max-height 0.3s ease"
            el.style.maxHeight = "0"
            el.classList.remove("hidden")
            el.parentNode.setAttribute('data-sidebar-index', index)
        })
    }

    restoreOpenState() {
        const savedIndex = localStorage.getItem(this.storageKeyValue)
        if (savedIndex !== null) {
            const savedElement = this.element.querySelector(`[data-sidebar-index="${savedIndex}"] .expand-field`)
            if (savedElement) {
                this.openExpandField(savedElement)
            }
        }
    }

    stopPropagation(event) {
        event.stopPropagation()
    }

    toggleMenu(event) {
        let parentNode = event.target
        while (parentNode && !parentNode.hasAttribute('data-sidebar-index')) {
            parentNode = parentNode.parentNode
            if (parentNode === this.element) break
        }

        const hiddenField = parentNode?.querySelector(".expand-field")
        if (!hiddenField) return

        const currentIndex = parentNode.getAttribute('data-sidebar-index')
        const isCurrentlyOpen = this.isOpen(hiddenField)

        this.isTogglingMenu = true
        this.closeAllExpandFields()

        if (!isCurrentlyOpen) {
            this.openExpandField(hiddenField)
            this.saveOpenState(currentIndex)
        } else {
            this.clearOpenState()
        }

        setTimeout(() => {
            this.isTogglingMenu = false
        }, 300)
    }

    closeAllExpandFields() {
        const allExpandFields = this.element.querySelectorAll(".expand-field")
        allExpandFields.forEach(field => {
            field.style.maxHeight = "0"
        })
    }

    openExpandField(field) {
        field.style.maxHeight = field.scrollHeight + "px"
    }

    saveOpenState(index) {
        localStorage.setItem(this.storageKeyValue, index)
    }

    clearOpenState() {
        localStorage.removeItem(this.storageKeyValue)
    }
}
